import * as THREE from 'three';

export interface CharAnimState {
  velocityXZ: number;   // horizontal speed magnitude m/s
  isSprinting: boolean;
  isAirborne: boolean;
  isCrouching: boolean;
  isShooting: boolean;  // true for one frame on fire
  isDead: boolean;
  deathTime: number;    // seconds since death (0 if alive)
}

export class FpsCharacterAnimator {
  private character: THREE.Group;
  private animTime = 0;
  private shootKickTimer = 0;

  // Cached limb refs
  private leftLeg: THREE.Object3D | null = null;
  private rightLeg: THREE.Object3D | null = null;
  private leftArm: THREE.Object3D | null = null;
  private rightArm: THREE.Object3D | null = null;
  private torso: THREE.Object3D | null = null;

  constructor(character: THREE.Group) {
    this.character = character;
    this.leftLeg  = character.getObjectByName('leftLeg')  ?? null;
    this.rightLeg = character.getObjectByName('rightLeg') ?? null;
    this.leftArm  = character.getObjectByName('leftArm')  ?? null;
    this.rightArm = character.getObjectByName('rightArm') ?? null;
    this.torso    = character.getObjectByName('torso')    ?? null;
  }

  update(dt: number, state: CharAnimState): void {
    this.animTime += dt;

    // --- Death animation ---
    if (state.isDead) {
      const t = Math.min(state.deathTime / 0.55, 1);
      if (this.torso)    this.torso.rotation.x    = -1.4 * t;
      if (this.leftLeg)  this.leftLeg.rotation.z  =  0.3 * t;
      if (this.rightLeg) this.rightLeg.rotation.z = -0.3 * t;
      if (this.leftArm)  this.leftArm.rotation.x  = lerp(this.leftArm.rotation.x,  0.5 * t, 0.12);
      if (this.rightArm) this.rightArm.rotation.x = lerp(this.rightArm.rotation.x, 0.5 * t, 0.12);
      return;
    }

    // --- Shoot kick ---
    if (state.isShooting) {
      this.shootKickTimer = 0.12;
      if (this.rightArm) this.rightArm.rotation.x -= 0.22;
    }
    if (this.shootKickTimer > 0) {
      this.shootKickTimer -= dt;
      if (this.rightArm) {
        this.rightArm.rotation.x = lerp(this.rightArm.rotation.x, 0, dt * 10);
      }
    }

    // --- Airborne ---
    if (state.isAirborne) {
      if (this.leftArm)  this.leftArm.rotation.x  = lerp(this.leftArm.rotation.x,  -0.45, dt * 8);
      if (this.rightArm && this.shootKickTimer <= 0) this.rightArm.rotation.x = lerp(this.rightArm.rotation.x, -0.45, dt * 8);
      if (this.leftLeg)  this.leftLeg.rotation.x  = lerp(this.leftLeg.rotation.x,   0.2,  dt * 8);
      if (this.rightLeg) this.rightLeg.rotation.x = lerp(this.rightLeg.rotation.x,  0.2,  dt * 8);
      if (this.torso)    this.torso.rotation.x     = lerp(this.torso.rotation.x,     0,    dt * 6);
      return;
    }

    // --- Walk / Run / Sprint ---
    const speed = state.velocityXZ;
    if (speed > 0.5) {
      const sprintMult = state.isSprinting ? 1.3 : 1.0;
      const cadence    = 2.8 + speed * 0.4;
      const magnitude  = 0.45 * Math.min(speed / 6, 1) * sprintMult;
      const swing      = Math.sin(this.animTime * cadence) * magnitude;

      if (this.leftLeg)  this.leftLeg.rotation.x  =  swing;
      if (this.rightLeg) this.rightLeg.rotation.x  = -swing;
      if (this.leftArm)  this.leftArm.rotation.x   = lerp(this.leftArm.rotation.x,  -swing * 0.7, dt * 14);
      if (this.rightArm && this.shootKickTimer <= 0) this.rightArm.rotation.x = lerp(this.rightArm.rotation.x, swing * 0.7, dt * 14);

      // Torso sprint lean
      const targetLean = state.isSprinting ? 0.12 : 0;
      if (this.torso) this.torso.rotation.x = lerp(this.torso.rotation.x, targetLean, dt * 6);
    } else {
      // Idle — return all limbs to 0
      if (this.leftLeg)  this.leftLeg.rotation.x  = lerp(this.leftLeg.rotation.x,  0, dt * 6);
      if (this.rightLeg) this.rightLeg.rotation.x = lerp(this.rightLeg.rotation.x, 0, dt * 6);
      if (this.leftArm)  this.leftArm.rotation.x  = lerp(this.leftArm.rotation.x,  0, dt * 6);
      if (this.rightArm && this.shootKickTimer <= 0) this.rightArm.rotation.x = lerp(this.rightArm.rotation.x, 0, dt * 6);
      if (this.torso)    this.torso.rotation.x     = lerp(this.torso.rotation.x,    0, dt * 6);
    }

    // Z-rotation cleanup
    if (this.leftLeg)  this.leftLeg.rotation.z  = lerp(this.leftLeg.rotation.z,  0, dt * 8);
    if (this.rightLeg) this.rightLeg.rotation.z = lerp(this.rightLeg.rotation.z, 0, dt * 8);
  }
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
